import * as Sentry from "@sentry/react";
import axios, {type AxiosRequestConfig, type AxiosResponse} from "axios";
import {isCompanyChurnedError} from "../utils/companyChurned.util.js";

export interface DiagnosticContext {
  operationId?: string;
  step?: string;
  fileSize?: number;
  fileType?: string;
  attemptId?: string;
  startedAt?: string;
  durationMs?: number;
  timeoutMs?: number;
  method?: string;
  route?: string;
  online?: boolean | "unknown";
  visibility?: string;
  classification?: "http" | "timeout" | "cancelled" | "network" | "local";
  code?: string;
  status?: number;
}

const contexts = new WeakMap<object, DiagnosticContext>();
const reported = new WeakSet<object>();
const attempts = new WeakMap<object, {context: DiagnosticContext; started: number}>();

export function createDiagnosticOperationId(): string {
  return crypto.randomUUID();
}

function observedState() {
  return {
    online:
      typeof navigator === "undefined" || typeof navigator.onLine !== "boolean"
        ? ("unknown" as const)
        : navigator.onLine,
    visibility: typeof document === "undefined" ? "unknown" : document.visibilityState,
  };
}

function attachContext(error: unknown, context: DiagnosticContext) {
  if (typeof error === "object" && error !== null) {
    contexts.set(error, {...context, ...contexts.get(error)});
  }
}

function getContext(error: unknown): DiagnosticContext {
  return typeof error === "object" && error !== null ? (contexts.get(error) ?? {}) : {};
}

function responseIsHtml(response: AxiosResponse): boolean {
  const contentType = String(response.headers["content-type"] ?? "")
    .split(";")[0]
    ?.trim();
  if (contentType !== "text/html") {
    return false;
  }
  let data: unknown = response.data;
  if (Object.prototype.toString.call(data) === "[object ArrayBuffer]") {
    const bytes = data as ArrayBuffer;
    data = new TextDecoder().decode(bytes.slice(0, 1024));
  }
  return typeof data === "string" && /^\s*(?:<!doctype html\b|<html\b)/i.test(data);
}

function classification(error: unknown): DiagnosticContext {
  if (axios.isAxiosError(error)) {
    return {
      classification: error.response
        ? "http"
        : error.code === "ETIMEDOUT"
          ? "timeout"
          : axios.isCancel(error)
            ? "cancelled"
            : "network",
      code: error.code,
      status: error.response?.status,
    };
  }
  return {
    classification: error instanceof Error && error.name === "AbortError" ? "cancelled" : "local",
  };
}

export function reportBrowserError(error: unknown, source: string, context?: DiagnosticContext) {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (
      status === 401 ||
      (status === 400 && (!error.response || !responseIsHtml(error.response))) ||
      isCompanyChurnedError(error, status)
    ) {
      return;
    }
  }
  if (typeof error === "object" && error !== null) {
    if (reported.has(error)) {
      return;
    }
    reported.add(error);
  }
  const diagnostic = {
    ...observedState(),
    ...classification(error),
    ...context,
    ...getContext(error),
  };
  const messages = {
    http: "HTTP failure",
    timeout: "Request timeout",
    cancelled: "Request cancelled",
    network: "Network failure",
    local: "Browser operation failed",
  };
  const safeError = new Error(messages[diagnostic.classification ?? "local"]);
  if (error instanceof Error) {
    safeError.stack = error.stack;
  }
  Sentry.captureException(safeError, {
    tags: {source, "diagnostic.managed": "true"},
    contexts: {diagnostic},
  });
}

export async function withDiagnosticStep<T>(
  operationId: string,
  step: string,
  action: () => Promise<T>,
  context?: DiagnosticContext
): Promise<T> {
  const started = performance.now();
  const diagnostic = {
    ...observedState(),
    ...context,
    operationId,
    step,
    startedAt: new Date().toISOString(),
  };
  try {
    const result = await action();
    Sentry.addBreadcrumb({
      category: "diagnostic.step",
      data: {...diagnostic, durationMs: performance.now() - started, outcome: "success"},
    });
    return result;
  } catch (error) {
    const failure = {...diagnostic, durationMs: performance.now() - started};
    attachContext(error, failure);
    Sentry.addBreadcrumb({category: "diagnostic.step", data: {...failure, outcome: "failure"}});
    throw error;
  }
}

function resolveUrl(url: string) {
  try {
    return new URL(url, typeof document === "undefined" ? "http://localhost/" : document.baseURI);
  } catch {
    return undefined;
  }
}

function isOwnApi(url: URL | undefined) {
  return (
    !!url &&
    typeof location !== "undefined" &&
    url.origin === location.origin &&
    (url.pathname === "/api" || url.pathname.startsWith("/api/"))
  );
}

function startAttempt(target: object, url: URL | undefined, method: string, timeoutMs?: number) {
  const context: DiagnosticContext = {
    ...observedState(),
    attemptId: createDiagnosticOperationId(),
    startedAt: new Date().toISOString(),
    method: method.toUpperCase(),
    route: normalizeDiagnosticRoute(url?.href ?? ""),
    timeoutMs,
  };
  attempts.set(target, {context, started: performance.now()});
  return context;
}

export function normalizeDiagnosticRoute(input: string): string {
  const url = resolveUrl(input);
  if (!url || !isOwnApi(url)) {
    return "external";
  }
  const path = url.pathname;
  if (/^\/api\/chimney-sweep-certificate\/customer\/[^/]+(?:\/latest)?$/.test(path)) {
    return path.endsWith("/latest")
      ? "/api/chimney-sweep-certificate/customer/:id/latest"
      : "/api/chimney-sweep-certificate/customer/:id";
  }
  if (/^\/api\/chimney-sweep-certificate\/[^/]+(?:\/(?:file|resend))?$/.test(path)) {
    return `/api/chimney-sweep-certificate/:id${path.endsWith("/file") ? "/file" : path.endsWith("/resend") ? "/resend" : ""}`;
  }
  if (/^\/api\/documents\/(?:text|upload-url)$/.test(path)) {
    return path;
  }
  if (/^\/api\/documents\/[^/]+(?:\/download-url)?$/.test(path)) {
    return `/api/documents/:id${path.endsWith("/download-url") ? "/download-url" : ""}`;
  }
  return "/api/[route]";
}

export function recordDiagnosticOutcome(operationId: string, step: string, outcome: string) {
  Sentry.addBreadcrumb({category: "diagnostic.step", data: {operationId, step, outcome}});
}

export function startAxiosDiagnostic(config: AxiosRequestConfig) {
  const url = resolveUrl(axios.getUri(config));
  const context = startAttempt(config, url, config.method ?? "GET", config.timeout);
  if (isOwnApi(url) && config.headers) {
    config.headers["X-Client-Request-Id"] = context.attemptId;
  }
}

export function finishAxiosDiagnostic(
  config: AxiosRequestConfig | undefined,
  error?: unknown,
  status?: number
) {
  if (!config) {
    return;
  }
  const attempt = attempts.get(config);
  if (!attempt) {
    return;
  }
  attempts.delete(config);
  const context = {
    ...attempt.context,
    ...observedState(),
    durationMs: performance.now() - attempt.started,
    ...(error ? classification(error) : {status, classification: "http" as const}),
  };
  if (error) {
    attachContext(error, context);
  }
  Sentry.addBreadcrumb({
    category: "diagnostic.request",
    data: {...context, outcome: error ? "failure" : "success"},
  });
}

export async function diagnosticFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  context?: DiagnosticContext
): Promise<Response> {
  const target = {};
  const request = typeof Request !== "undefined" && input instanceof Request ? input : undefined;
  const url = resolveUrl(request?.url ?? String(input));
  const diagnostic = {
    ...context,
    ...startAttempt(target, url, init?.method ?? request?.method ?? "GET"),
  };
  const started = performance.now();
  const headers = new Headers(init?.headers ?? request?.headers);
  if (isOwnApi(url) && diagnostic.attemptId) {
    headers.set("X-Client-Request-Id", diagnostic.attemptId);
  }
  let status: number | undefined;
  try {
    const response = await fetch(input, {...init, headers});
    status = response.status;
    if (!response.ok) {
      throw new Error("HTTP request failed");
    }
    Sentry.addBreadcrumb({
      category: "diagnostic.request",
      data: {...diagnostic, status, durationMs: performance.now() - started, outcome: "success"},
    });
    return response;
  } catch (error) {
    const failure: DiagnosticContext = {
      ...diagnostic,
      ...observedState(),
      status,
      durationMs: performance.now() - started,
      classification:
        status !== undefined
          ? "http"
          : (init?.signal ?? request?.signal)?.aborted
            ? "cancelled"
            : "network",
    };
    attachContext(error, failure);
    Sentry.addBreadcrumb({category: "diagnostic.request", data: {...failure, outcome: "failure"}});
    throw error;
  }
}
